import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import type {Document} from '@contentful/rich-text-types'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import {
  Box,
  Grid,
  Heading,
  InlineLink,
  Label,
  OrderedList,
  Stack,
  Text,
  ThemeProvider,
  UnorderedList,
  VideoPlayer,
} from '@primer/react-brand'
import {getAnalyticsEvent, documentToPlainTextString} from '@github-ui/swp-core/lib/utils/analytics'
import {FeatureList} from '@github-ui/swp-core/components/FeatureList/FeatureList'
import type {IconFeatureList} from '@github-ui/swp-core/schemas/contentful/contentTypes/iconFeatureList'
import {IframeVideo} from '@github-ui/swp-core/components/IframeVideo/IframeVideo'
import GlowCircleBackground from './components/Background/GlowCircleBackground'
import type {ContactSalesPage} from '../../lib/types/contentful'
import {Highlight} from './components/Highlight'
import {ContactSalesFormSwp} from './components/Form'
import styles from './ContactSalesTemplate.module.css'

type ContactSalesTemplateProps = {
  page: ContactSalesPage
}

export function ContactSalesTemplate({page}: ContactSalesTemplateProps) {
  const {
    fields: {template},
  } = page

  return (
    <ThemeProvider
      className="ContactSales__Page position-relative ws-pre-wrap border-bottom borderColor-muted"
      colorMode="dark"
    >
      <Grid fullWidth className="ContactSales__DesktopBackground height-full position-absolute p-0">
        <GlowCircleBackground branding={template.fields.branding} />

        <Grid.Column span={{xsmall: 12, medium: 6}} start={{xsmall: 1, medium: 7}} className="position-relative">
          <ThemeProvider className="height-full" colorMode={page.fields.settings?.fields.colorMode ?? 'light'} />
        </Grid.Column>
      </Grid>

      <Grid className="position-relative p-0 mt-0">
        <Grid.Column span={{medium: 6}} className={styles.infoPane}>
          <Box
            padding={16}
            paddingBlockStart={{narrow: 64, regular: 128, wide: 128}}
            paddingBlockEnd={{narrow: 24, regular: 80}}
            marginBlockStart={{wide: 128}}
            className="pr-xl-0 position-sticky top-0 mt-0"
          >
            <Stack direction="vertical" gap="none" padding="none" alignItems="flex-start">
              <Stack direction="vertical" padding="none" gap={16} alignItems="flex-start">
                {template.fields.label && <Label>{template.fields.label}</Label>}

                <Heading as="h1" size="3" className={`pr-mb-4 ${styles.heading}`}>
                  {template.fields.headline}
                </Heading>
              </Stack>

              {template.fields.body ? (
                <Stack direction="vertical" gap={'none'} padding="none" alignItems="flex-start">
                  {documentToReactComponents(template.fields.body, {
                    renderNode: {
                      [BLOCKS.HEADING_2]: (_, children) => (
                        <Heading as="h2" className="pb-2 pr-pt-6" size="subhead-medium">
                          {children}
                        </Heading>
                      ),
                      [BLOCKS.PARAGRAPH]: (_, children) => (
                        <Text as="p" className="pr-mb-4">
                          {children}
                        </Text>
                      ),
                      [BLOCKS.UL_LIST]: node => {
                        const unTagged = documentToReactComponents(node as Document, {
                          renderNode: {
                            [BLOCKS.UL_LIST]: (_, children) => children,
                            [BLOCKS.PARAGRAPH]: (_, children) => children,
                            [BLOCKS.LIST_ITEM]: (_, children) => (
                              <UnorderedList.Item className={styles.checkIconColor}>{children}</UnorderedList.Item>
                            ),
                          },
                        })
                        return (
                          <UnorderedList variant="checked" className="pr-mb-4">
                            {unTagged}
                          </UnorderedList>
                        )
                      },
                      [BLOCKS.OL_LIST]: node => {
                        const unTagged = documentToReactComponents(node as Document, {
                          renderNode: {
                            [BLOCKS.OL_LIST]: (_, children) => children,
                            [BLOCKS.PARAGRAPH]: (_, children) => children,
                            [BLOCKS.LIST_ITEM]: (_, children) => <OrderedList.Item>{children}</OrderedList.Item>,
                          },
                        })
                        return <OrderedList className="pr-mb-4">{unTagged}</OrderedList>
                      },
                      [INLINES.HYPERLINK]: (node, children) => (
                        <InlineLink
                          {...getAnalyticsEvent({
                            action: documentToPlainTextString(node as Document),
                            tag: 'hyperlink',
                            location: template.fields.headline,
                          })}
                          href={node.data.uri}
                          className={styles.link}
                        >
                          {children}
                        </InlineLink>
                      ),
                      [BLOCKS.EMBEDDED_ENTRY]: node => {
                        if (node.data.target.sys.contentType.sys.id === 'video') {
                          const {coverImage, videoSrc, videoAsset, videoTitle} = node.data.target.fields

                          if (videoSrc) {
                            return <IframeVideo videoSrc={videoSrc} heading={videoTitle} image={coverImage} />
                          }

                          if (videoAsset) {
                            return (
                              <VideoPlayer title={videoTitle} poster={coverImage?.fields.file.url}>
                                <VideoPlayer.Source
                                  src={videoAsset.fields.file.url}
                                  type={videoAsset.fields.file.contentType}
                                />
                              </VideoPlayer>
                            )
                          }
                        }

                        // New embeddedable iconFeatureList
                        if (node.data.target.sys.contentType.sys.id === 'iconFeatureList') {
                          const entry = node.data.target as IconFeatureList

                          return (
                            <FeatureList
                              items={entry.fields.items}
                              className="pr-mb-6"
                              iconClassName={styles.iconFeatureListIcon}
                            />
                          )
                        }
                        return null
                      },
                    },
                  })}
                </Stack>
              ) : null}

              {template.fields.highlight ? <Highlight entry={template.fields.highlight} /> : null}
            </Stack>
          </Box>
        </Grid.Column>

        <Grid.Column span={{medium: 6}} start={{xlarge: 8}}>
          <ThemeProvider colorMode={page.fields.settings?.fields.colorMode ?? 'light'}>
            <Box
              padding={24}
              paddingBlockStart={{
                narrow: 64,
                regular: 128,
                wide: 'none',
              }}
              paddingBlockEnd={{
                narrow: 24,
                regular: 80,
              }}
              marginBlockStart={{
                wide: 128,
              }}
              className="pl-xl-0"
            >
              {template.fields.form ? (
                <ContactSalesFormSwp
                  component={template.fields.form}
                  formRequirementMessage={template.fields.formRequirementMessage}
                  redirectUrl={template.fields.redirect}
                />
              ) : null}
            </Box>
          </ThemeProvider>
        </Grid.Column>
      </Grid>
    </ThemeProvider>
  )
}
